/* assets/pwa-installer.js — SITE-WIDE-PWA-RUNTIME-30
 * Owner: single browser PWA lifecycle for glhksolution.com.
 * Scope: one root service worker and one installer owner with product-specific
 * install UI projection for LittleG and GTender, installed-state suppression
 * and update handoff.
 * Note: no product data, checkout, account, GFun, Mission Engine or API ownership.
 */
(function () {
  'use strict';

  var VERSION = '30';
  function canonicalEndpoint(value, fallback) {
    var resolved = String(value || fallback || '').trim();
    var queryIndex = resolved.indexOf('?');
    return queryIndex === -1 ? resolved : resolved.slice(0, queryIndex);
  }
  function isGTenderPathname(pathname) {
    return /(?:^|\/)pages\/gtender\/?$/i.test(String(pathname || ''));
  }

  var IS_GTENDER = isGTenderPathname(window.location && window.location.pathname);
  var PWA_PRODUCT = IS_GTENDER ? 'gtender' : 'littleg';
  var APP_TITLE = IS_GTENDER ? 'GTender' : 'LittleG';
  var MANIFEST_URL = canonicalEndpoint(
    IS_GTENDER ? '/apps/gcity-api/pwa/gtender-manifest.webmanifest' : window.__GCITY_MANIFEST_ROOT_URL,
    IS_GTENDER ? '/apps/gcity-api/pwa/gtender-manifest.webmanifest' : '/apps/gcity-api/pwa/manifest.webmanifest'
  );
  var SW_URL = canonicalEndpoint(window.__GCITY_SW_ROOT_URL, '/apps/gcity-api/pwa/service-worker.js');
  var APPLE_ICON_URL = String(
    window.__GCITY_APPLE_TOUCH_ICON_URL
      || (IS_GTENDER
        ? '/apps/gcity-api/pwa/icons/avatar-business-professional-180.png'
        : '/apps/gcity-api/pwa/icons/avatar-site-icon-256.png')
  ).trim();
  var STYLE_URL = String(window.__GCITY_PWA_INSTALLER_STYLE_URL || '').trim();
  var PROMPT_ID = 'pwa-install-section';
  var UPDATE_ID = 'pwa-update-banner';
  var DISMISS_KEY = PWA_PRODUCT + ':pwa:dismissedAt';
  var INSTALLED_KEY = PWA_PRODUCT + ':pwa:installed';
  var DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
  var UPDATE_INTERVAL = 4 * 60 * 60 * 1000;
  // #1016: install promotion is retired across LittleG and GTender; PWA updates remain supported.
  var INSTALL_PROMPTS_ENABLED = false;

  function isNativeAppSurface() {
    try {
      if (window.__LITTLEG_PLATFORM__ === 'ios' || window.__LITTLEG_PLATFORM__ === 'android') return true;
      return Boolean(window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
    } catch (_) { return false; }
  }

  if (window.__GCITY_DISABLE_PWA === true || isNativeAppSurface()) return;
  if (window.__GCITY_PWA_INSTALLER_BOUND__ === true) return;
  window.__GCITY_PWA_INSTALLER_BOUND__ = true;
  window.__GCITY_PWA_SITE_WIDE = true;

  var deferredPrompt = null;
  var registration = null;
  var promptVisible = false;
  var updatePending = false;
  var showTimer = 0;
  var reloading = false;
  var kidMapValueGateBlocked = false;

  function root() { return document.documentElement; }
  function byId(id) { return document.getElementById(id); }
  function now() { return Date.now ? Date.now() : new Date().getTime(); }
  function safeStorage() { try { return window.localStorage; } catch (_) { return null; } }
  function read(key) { try { var store = safeStorage(); return store && store.getItem(key); } catch (_) { return null; } }
  function write(key, value) { try { var store = safeStorage(); if (store) store.setItem(key, String(value)); } catch (_) {} }
  function remove(key) { try { var store = safeStorage(); if (store) store.removeItem(key); } catch (_) {} }
  function isZh() { return /^zh(?:-|$)/i.test(String(document.documentElement.lang || navigator.language || '')); }
  function isIOS() {
    try {
      var ua = String(navigator.userAgent || '');
      return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    } catch (_) { return false; }
  }
  function isStandalone() {
    try { return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true; }
    catch (_) { return false; }
  }
  function isInstalled() { return isStandalone() || read(INSTALLED_KEY) === '1'; }
  function isDismissed() {
    var ts = parseInt(read(DISMISS_KEY) || '0', 10);
    return Number.isFinite(ts) && ts > 0 && now() - ts < DISMISS_MS;
  }
  function setAttr(name, value) { try { root().setAttribute(name, String(value)); } catch (_) {} }

  function isKidMapSurface() {
    return Boolean(
      document.querySelector('[data-littleg-kid-map]')
      || document.body && document.body.getAttribute('data-surface') === 'kid-map'
    );
  }
  function hasKidMapValue() {
    if (document.querySelector('[data-littleg-profile-customer="true"]')) return true;
    var profile = window.LittleGProfile;
    if (profile && typeof profile.get === 'function') {
      try { if (profile.get()) return true; } catch (_) {}
    }
    var family = window.LittleGFamilyData;
    var graph = family && family.socialGraph;
    if (!graph || typeof graph.privateSummary !== 'function') return false;
    try {
      var summary = graph.privateSummary();
      return Boolean(summary && Number(summary.total_count || 0) > 0);
    } catch (_) { return false; }
  }
  function isLittleGInstallSurface() {
    return window.__GCITY_PWA_FAMILY_SURFACE === true
      || document.documentElement.classList.contains('littleg-app');
  }
  function canPromptOnCurrentSurface() {
    if (IS_GTENDER) {
      setAttr('data-pwa-prompt-gate', 'gtender-visible-install');
      return true;
    }
    if (!isKidMapSurface()) {
      if (isLittleGInstallSurface()) return true;
      setAttr('data-pwa-prompt-gate', 'non-littleg-surface');
      return false;
    }
    if (kidMapValueGateBlocked) return false;
    if (hasKidMapValue()) {
      setAttr('data-pwa-prompt-gate', 'kid-map-value-earned');
      return true;
    }
    kidMapValueGateBlocked = true;
    setAttr('data-pwa-prompt-gate', 'kid-map-first-value');
    return false;
  }
  let gtenderReserveFrame = 0;
  let gtenderObservedPrompt = null;
  let gtenderPromptResizeObserver = null;

  function syncGTenderPromptSpace() {
    if (!IS_GTENDER) return;

    const surface = document.querySelector('[data-gtender-public-entry]');
    const footer = document.querySelector('[data-footer-contract="GLOBAL-FOOTER-TRUST-02"]');
    const prompt = byId(PROMPT_ID);    if (!surface) return;

    if (
      prompt !== gtenderObservedPrompt &&
      typeof ResizeObserver === 'function'
    ) {
      if (gtenderPromptResizeObserver) {
        gtenderPromptResizeObserver.disconnect();
      }

      gtenderObservedPrompt = prompt;
      gtenderPromptResizeObserver = null;

      if (prompt) {
        gtenderPromptResizeObserver = new ResizeObserver(
          scheduleGTenderPromptSpace
        );
        gtenderPromptResizeObserver.observe(prompt);
      }
    }

    if (!prompt || prompt.hidden) {
      surface.style.removeProperty('--gtender-pwa-reserve');
      if (footer) footer.style.removeProperty('--gtender-pwa-reserve');
      return;
    }

    const rect = prompt.getBoundingClientRect();
    const reserve = rect.height > 0
      ? Math.ceil(Math.max(0, window.innerHeight - rect.top) + 16)
      : 0;

    if (reserve > 0) {
      surface.style.setProperty('--gtender-pwa-reserve', reserve + 'px');
      if (footer) {
        footer.style.setProperty('--gtender-pwa-reserve', reserve + 'px');
      }
    } else {
      surface.style.removeProperty('--gtender-pwa-reserve');
      if (footer) footer.style.removeProperty('--gtender-pwa-reserve');
    }
  }

  function scheduleGTenderPromptSpace() {
    if (!IS_GTENDER || gtenderReserveFrame) return;

    gtenderReserveFrame = window.requestAnimationFrame(() => {
      gtenderReserveFrame = 0;
      syncGTenderPromptSpace();
    });
  }

  if (IS_GTENDER) {
    window.addEventListener('resize', scheduleGTenderPromptSpace, {
      passive: true
    });
  }

  function hide(node) {
    if (!node) return;
    node.hidden = true;
    node.setAttribute('aria-hidden', 'true');
    scheduleGTenderPromptSpace();
  }

  function show(node) {
    if (!node) return;
    node.hidden = false;
    node.setAttribute('aria-hidden', 'false');
    scheduleGTenderPromptSpace();
  }

  function ensureStyle() {
    if (!STYLE_URL || document.getElementById('SiteWidePwaInstallerStyle')) return;
    var link = document.createElement('link');
    link.id = 'SiteWidePwaInstallerStyle';
    link.rel = 'stylesheet';
    link.href = STYLE_URL;
    link.setAttribute('data-style-owner', 'assets/pwa-installer.css');
    document.head.appendChild(link);
  }

  function ensureSingleLink(rel, href, owner) {
    var nodes = Array.prototype.slice.call(document.querySelectorAll('link[rel="' + rel + '"]'));
    var canonical = nodes.find(function (node) { return node.getAttribute('data-pwa-owner') === owner; }) || nodes[0];
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = rel;
      document.head.appendChild(canonical);
    }
    canonical.href = href;
    canonical.setAttribute('data-pwa-owner', owner);
    nodes.forEach(function (node) { if (node !== canonical) node.remove(); });
    return canonical;
  }

  function ensureSingleMeta(name, content) {
    var nodes = Array.prototype.slice.call(document.querySelectorAll('meta[name="' + name + '"]'));
    var canonical = nodes.find(function (node) { return node.getAttribute('data-pwa-owner') === 'site-wide'; }) || nodes[0];
    if (!canonical) {
      canonical = document.createElement('meta');
      canonical.name = name;
      document.head.appendChild(canonical);
    }
    canonical.content = content;
    canonical.setAttribute('data-pwa-owner', 'site-wide');
    nodes.forEach(function (node) { if (node !== canonical) node.remove(); });
    return canonical;
  }

  function normalizeHead() {
    var manifestOwner = IS_GTENDER ? 'assets/gtender-manifest.json' : 'assets/manifest.json';
    var iconOwner = IS_GTENDER ? 'assets/avatar-business-professional-180.png' : 'assets/avatar-site-icon-256.png';
    var manifest = ensureSingleLink('manifest', MANIFEST_URL, manifestOwner);
    manifest.setAttribute('data-pwa-manifest-owner', manifestOwner);
    manifest.setAttribute('data-pwa-product', PWA_PRODUCT);
    var icon = ensureSingleLink('apple-touch-icon', APPLE_ICON_URL, iconOwner);
    icon.type = 'image/png';
    icon.sizes = IS_GTENDER ? '180x180' : '256x256';
    icon.setAttribute('data-pwa-icon-owner', iconOwner);
    icon.setAttribute('data-pwa-product', PWA_PRODUCT);
    ensureSingleMeta('mobile-web-app-capable', 'yes');
    ensureSingleMeta('apple-mobile-web-app-capable', 'yes');
    ensureSingleMeta('apple-mobile-web-app-title', APP_TITLE);
    ensureSingleMeta('apple-mobile-web-app-status-bar-style', 'default');
    setAttr('data-pwa-manifest-owner', manifestOwner);
    setAttr('data-pwa-icon-owner', iconOwner);
    setAttr('data-pwa-identity', IS_GTENDER ? 'gtender' : 'site-wide');
    setAttr('data-pwa-product', PWA_PRODUCT);
  }

  function ensurePromptDom() {
    var prompt = byId(PROMPT_ID);
    if (!prompt) {
      prompt = document.createElement('section');
      prompt.id = PROMPT_ID;
      prompt.className = 'pwa-install-section';
      prompt.hidden = true;
      prompt.setAttribute('aria-hidden', 'true');
      prompt.innerHTML = '<div class="pwa-install-card" role="dialog" aria-modal="false" aria-labelledby="pwa-install-title" aria-describedby="pwa-install-description"><button class="pwa-dismiss" type="button" data-action="pwa-dismiss" aria-label="Close">'+((window.LittleGControlIcons&&window.LittleGControlIcons.close)||'×')+'</button><div class="pwa-install-content"><span class="pwa-icon" aria-hidden="true">G</span><div class="pwa-install-copy"><p class="pwa-title" id="pwa-install-title" data-pwa-title></p><p class="pwa-description" id="pwa-install-description" data-pwa-description></p><p class="pwa-ios-hint" data-pwa-install-hint hidden></p></div><div class="pwa-install-actions"><button class="pwa-install-btn" type="button" data-action="pwa-install"><span data-pwa-primary-label></span></button><button class="pwa-dismiss-link" type="button" data-action="pwa-dismiss"><span data-pwa-dismiss-label></span></button></div></div></div>';
      document.body.appendChild(prompt);
    }
    prompt.setAttribute('data-pwa-install-prompt', PWA_PRODUCT);
    prompt.setAttribute('data-pwa-product', PWA_PRODUCT);
    prompt.classList.toggle('pwa-install-section--gtender', IS_GTENDER);
    prompt.toggleAttribute('data-pwa-compact', IS_GTENDER);

    var promptIcon = prompt.querySelector('.pwa-icon');
    if (promptIcon) {
      if (IS_GTENDER) {
        var iconImage = promptIcon.querySelector('.pwa-icon-image');
        if (!iconImage) {
          promptIcon.textContent = '';
          iconImage = document.createElement('img');
          iconImage.className = 'pwa-icon-image';
          iconImage.alt = '';
          iconImage.setAttribute('aria-hidden', 'true');
          iconImage.decoding = 'async';
          promptIcon.appendChild(iconImage);
        }
        iconImage.src = APPLE_ICON_URL;
      } else {
        promptIcon.textContent = 'G';
      }
    }

    ensureUpdateDom();
    updateCopy();
    return prompt;
  }

  function ensureUpdateDom() {
    var update = byId(UPDATE_ID);
    if (!update) {
      update = document.createElement('div');
      update.id = UPDATE_ID;
      update.className = 'pwa-update-banner';
      update.hidden = true;
      update.setAttribute('aria-hidden', 'true');
      update.setAttribute('role', 'status');
      update.setAttribute('aria-live', 'polite');
      update.innerHTML = '<span data-pwa-update-copy></span><button type="button" data-action="pwa-update"></button>';
      document.body.appendChild(update);
    }
    return update;
  }

  function installAvailability() {
    if (isInstalled()) return 'installed';
    if (isIOS()) return 'ios';
    if (deferredPrompt) return 'prompt';
    return 'manual';
  }

  function updateCopy() {
    var zh = isZh();
    var prompt = byId(PROMPT_ID);
    var availability = installAvailability();
    if (prompt) {
      var title = prompt.querySelector('[data-pwa-title]');
      var desc = prompt.querySelector('[data-pwa-description]');
      var primary = prompt.querySelector('[data-pwa-primary-label]');
      var dismiss = prompt.querySelector('[data-pwa-dismiss-label]');
      var dismissButton = prompt.querySelector('.pwa-dismiss-link');
      var hint = prompt.querySelector('[data-pwa-install-hint]');
      if (IS_GTENDER) {
        if (title) title.textContent = zh ? '將 GTender 加入主畫面' : 'Add GTender to Home Screen';
        if (desc) desc.textContent = zh ? '快速查看香港最新招標更新。' : 'Quick access to live HK tender updates.';
        if (primary) primary.textContent = zh ? '加入主畫面' : 'Add to Home Screen';
        if (dismissButton) dismissButton.hidden = true;
        if (hint) {
          if (availability === 'ios') {
            hint.textContent = zh ? '點「分享」↑，再選「加入主畫面」。' : 'Tap Share ↑, then choose “Add to Home Screen”.';
          } else {
            hint.textContent = zh ? '請開啟瀏覽器選單，選擇「安裝應用程式」或「加入主畫面」。' : 'Open your browser menu and choose Install app or Add to Home Screen.';
          }
        }
      } else {
        if (title) title.textContent = zh ? '加入主畫面' : 'Add to Home Screen';
        if (desc) desc.textContent = zh ? '從主畫面快速打開 LittleG。' : 'Open LittleG faster from your Home Screen.';
        if (primary) primary.textContent = isIOS() ? (zh ? '查看步驟' : 'View steps') : (zh ? '安裝' : 'Install');
        if (dismiss) dismiss.textContent = zh ? '稍後' : 'Later';
        if (dismissButton) dismissButton.hidden = false;
        if (hint) hint.textContent = zh ? '點分享，再選「加入主畫面」。' : 'Tap Share, then Add to Home Screen.';
      }
      if (hint && !prompt.hasAttribute('data-pwa-instruction-visible')) hint.hidden = true;
      var close = prompt.querySelector('.pwa-dismiss');
      if (close) close.setAttribute('aria-label', zh ? '關閉' : 'Close');
      prompt.setAttribute('data-pwa-install-available', availability);
      setAttr('data-pwa-install-available', availability);
    }
    var update = byId(UPDATE_ID);
    if (update) {
      var copy = update.querySelector('[data-pwa-update-copy]');
      var button = update.querySelector('[data-action="pwa-update"]');
      if (copy) copy.textContent = zh ? 'LittleG 有新版本。' : 'A new LittleG version is available.';
      if (button) button.textContent = zh ? '更新' : 'Update';
    }
  }

  function canShowPrompt() {
    if (isInstalled() || promptVisible) return false;
    if (!INSTALL_PROMPTS_ENABLED) return false;
    if (!canPromptOnCurrentSurface()) return false;
    if (IS_GTENDER) return true;
    if (isDismissed()) return false;
    if (isIOS()) return true;
    return Boolean(deferredPrompt);
  }

  function hidePrompt() {
    hide(byId(PROMPT_ID));
    promptVisible = false;
  }

  function showPrompt() {
    if (!INSTALL_PROMPTS_ENABLED) { hidePrompt(); return; }
    ensureStyle();
    ensurePromptDom();
    normalizeHead();
    if (!canShowPrompt()) { hidePrompt(); return; }
    setAttr('data-pwa-prompt-gate', IS_GTENDER ? 'gtender-visible-install' : root().getAttribute('data-pwa-prompt-gate') || 'eligible');
    updateCopy();
    show(byId(PROMPT_ID));
    promptVisible = true;
  }

  function schedulePrompt(delay) {
    if (!INSTALL_PROMPTS_ENABLED) return;
    if (showTimer) clearTimeout(showTimer);
    showTimer = window.setTimeout(showPrompt, Math.max(0, Number(delay) || 0));
  }

  function revealInstallInstructions(prompt) {
    const hint = prompt && prompt.querySelector('[data-pwa-install-hint]');
    if (!hint) return;

    prompt.setAttribute('data-pwa-instruction-visible', 'true');
    hint.hidden = false;
    hint.setAttribute('tabindex', '-1');

    try {
      hint.focus({ preventScroll: true });
    } catch (_error) {
      hint.focus();
    }

    scheduleGTenderPromptSpace();
  }

  async function requestInstall(prompt) {
    if (!INSTALL_PROMPTS_ENABLED) return { status: 'disabled' };
    if (isInstalled()) return { status: 'installed' };
    if (isIOS() || !deferredPrompt) {
      revealInstallInstructions(prompt);
      updateCopy();
      return { status: 'instructions', reason: isIOS() ? 'ios' : 'unavailable' };
    }
    try {
      await deferredPrompt.prompt();
      var choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === 'accepted') write(INSTALLED_KEY, '1');
      deferredPrompt = null;
      if (isInstalled()) {
        hidePrompt();
        return { status: 'accepted' };
      }
      if (prompt) {
        prompt.removeAttribute('data-pwa-instruction-visible');
        updateCopy();
      }
      return { status: choice && choice.outcome || 'dismissed' };
    } catch (_) {
      revealInstallInstructions(prompt);
      return { status: 'error' };
    }
  }

  function bindPrompt() {
    var prompt = INSTALL_PROMPTS_ENABLED ? ensurePromptDom() : null;
    if (prompt && prompt.getAttribute('data-pwa-bound') !== 'true') {
      prompt.setAttribute('data-pwa-bound', 'true');
      prompt.addEventListener('click', async function (event) {
        var button = event.target && event.target.closest ? event.target.closest('[data-action]') : null;
        if (!button || !prompt.contains(button)) return;
        var action = button.getAttribute('data-action');
        if (action === 'pwa-dismiss') {
          if (
            IS_GTENDER &&
            prompt.hasAttribute('data-pwa-compact') &&
            prompt.hasAttribute('data-pwa-instruction-visible')
          ) {
            prompt.removeAttribute('data-pwa-instruction-visible');

            const hint = prompt.querySelector('[data-pwa-install-hint]');
            if (hint) hint.hidden = true;

            updateCopy();
            scheduleGTenderPromptSpace();

            const install = prompt.querySelector('[data-action="pwa-install"]');
            if (install) {
              try {
                install.focus({ preventScroll: true });
              } catch (_error) {
                install.focus();
              }
            }
            return;
          }

          if (!IS_GTENDER) write(DISMISS_KEY, now());
          hidePrompt();
          return;
        }
        if (action !== 'pwa-install') return;
        await requestInstall(prompt);
      });
    }

    var installButton = prompt && prompt.querySelector('.pwa-install-btn[data-action="pwa-install"]');
    if (installButton && installButton.getAttribute('data-pwa-install-bound') !== 'true') {
      installButton.setAttribute('data-pwa-install-bound', 'true');
      installButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        requestInstall(prompt);
      });
    }

    var update = ensureUpdateDom();
    if (update && update.getAttribute('data-pwa-bound') !== 'true') {
      update.setAttribute('data-pwa-bound', 'true');
      update.addEventListener('click', function (event) {
        var button = event.target && event.target.closest ? event.target.closest('[data-action="pwa-update"]') : null;
        if (!button || !update.contains(button)) return;
        var waiting = registration && registration.waiting;
        try { if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (_) {}
      });
    }
  }

  function showUpdateBanner() {
    if (IS_GTENDER) return;
    updatePending = true;
    var update = byId(UPDATE_ID);
    if (!update) return;
    updateCopy();
    show(update);
  }

  async function cleanupLegacyPwa() {
    if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
      try {
        var registrations = await navigator.serviceWorker.getRegistrations();
        var expectedScope = new URL('/', location.origin).toString();
        await Promise.all(registrations.map(function (reg) {
          var scriptUrl = String(reg && reg.active && reg.active.scriptURL || reg && reg.waiting && reg.waiting.scriptURL || reg && reg.installing && reg.installing.scriptURL || '');
          if (reg.scope !== expectedScope && scriptUrl.indexOf('gcitypartners-pwa') !== -1) return reg.unregister();
          return Promise.resolve();
        }));
      } catch (_) {}
    }
    if (window.caches && caches.keys) {
      try {
        var keys = await caches.keys();
        await Promise.all(keys.filter(function (key) { return key.indexOf('gcitypartners-pwa-') === 0; }).map(function (key) { return caches.delete(key); }));
      } catch (_) {}
    }
  }

  async function registerSW() {
    if (!('serviceWorker' in navigator)) { setAttr('data-pwa-scope', 'none'); return; }
    if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') { setAttr('data-pwa-scope', 'none'); return; }
    await cleanupLegacyPwa();
    try {
      registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
      var expectedScope = new URL('/', location.origin).toString();
      if (registration.scope !== expectedScope) {
        await registration.unregister();
        registration = null;
        setAttr('data-pwa-registration-error', 'non_root_scope');
        return;
      }
      setAttr('data-pwa-scope', 'root');
      registration.addEventListener('updatefound', function () {
        var worker = registration && registration.installing;
        if (!worker) return;
        worker.addEventListener('statechange', function () {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) showUpdateBanner();
        });
      });
      if (registration.waiting && navigator.serviceWorker.controller) showUpdateBanner();
      window.setInterval(function () { try { if (registration && registration.update) registration.update(); } catch (_) {} }, UPDATE_INTERVAL);
    } catch (error) {
      setAttr('data-pwa-scope', 'none');
      setAttr('data-pwa-registration-error', error && error.message || 'registration_failed');
    }
  }

  window.addEventListener('beforeinstallprompt', function (event) {
    event.preventDefault();
    deferredPrompt = event;
    if (!INSTALL_PROMPTS_ENABLED) return;
    setAttr('data-pwa-install-available', 'prompt');
    if (IS_GTENDER) {
      var prompt = byId(PROMPT_ID);
      if (prompt) {
        prompt.removeAttribute('data-pwa-instruction-visible');
        updateCopy();
      }
      schedulePrompt(0);
      return;
    }
    schedulePrompt(isKidMapSurface() ? 1600 : 700);
  });

  window.addEventListener('appinstalled', function () {
    deferredPrompt = null;
    write(INSTALLED_KEY, '1');
    remove(DISMISS_KEY);
    hidePrompt();
    setAttr('data-pwa-installed', 'true');
    setAttr('data-pwa-install-available', 'installed');
  });

  window.addEventListener('pageshow', function () {
    normalizeHead();
    if (isInstalled()) {
      write(INSTALLED_KEY, '1');
      hidePrompt();
      setAttr('data-pwa-install-available', 'installed');
    } else if (IS_GTENDER) {
      schedulePrompt(350);
    } else if (isIOS()) {
      schedulePrompt(isKidMapSurface() ? 1600 : 1200);
    }
  });

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', function () {
      if (updatePending && !reloading) {
        reloading = true;
        window.location.reload();
      }
    });
  }

  ensureStyle();
  normalizeHead();
  setAttr('data-pwa-install-promotion', 'disabled');
  hidePrompt();
  bindPrompt();
  if (isStandalone()) write(INSTALLED_KEY, '1');
  registerSW();
  if (!isInstalled()) {
    if (IS_GTENDER) schedulePrompt(350);
    else if (isIOS()) schedulePrompt(isKidMapSurface() ? 1600 : 1200);
  }
  setAttr('data-pwa-installer-version', VERSION);
  setAttr('data-pwa-runtime-ready', 'true');

  window.GCityPWA = Object.assign(window.GCityPWA || {}, {
    version: VERSION,
    product: PWA_PRODUCT,
    showInstallPrompt: showPrompt,
    hideInstallPrompt: hidePrompt,
    requestInstall: requestInstall,
    normalizeHead: normalizeHead,
    isInstalled: isInstalled,
    canPromptOnCurrentSurface: canPromptOnCurrentSurface
  });
})();